import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import SimpleButton from '../../components/SimpleButton';
import RoomItem from '../../components/RoomItem';
import { IApplicationState } from '../../store';
import { CustomLink, RoomsList } from './styles';
import CustomTable from '../../components/CustomTable';
import { ISweepTime } from '../../store/sweeps/types';
import { startEditingSweep, startEditingSweepMember, startEditingRegister, startEditingSweepRoom, sweepArchive, sweepExport, sweepGuestRemoveRequest, sweepRemoveRequest, sweepUnarchive, sweepWorkRegisterRemoveRequest } from '../../store/sweeps/slice';
import history from '../../providers/history';
import Spacer from '../../components/Spacer';
import ButtonGroup from '../../components/ButtonGroup';
import { systemAddDialog } from '../../store/system/slice';
import ErrorMessage from '../../components/ErrorMessage';
import { useTranslation } from 'react-i18next';
;

const SweepDetails: React.FC = (props) => {
    const route = useMatch('/sweep/:idSweep')

    const routeArchived = useMatch('/sweepArchived/:idSweep');

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const idSweep = route?.params.idSweep || routeArchived?.params.idSweep;

    const sweep = useSelector((state: IApplicationState) => state.sweeps.data.find(sweep => sweep.id === idSweep))

    const calculateTotal = (time: ISweepTime): string => {
        const totalTime = (time.endingAt ? time.endingAt.getTime() : new Date().getTime()) - time.beginingAt!.getTime()

        const hours = Math.floor(totalTime / (1000 * 60 * 60));
        const minutes = Math.floor(totalTime / (1000 * 60)) % 60;
        const seconds = Math.floor(totalTime / 1000) % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    if (!sweep) return <h1>{t("pages.sweeps.not_found")}</h1>;

    if (sweep.isDeleting) return <h1>{t("pages.sweeps.deleting")}</h1>;

    return (<>
        {sweep.error !== undefined && <ErrorMessage>{sweep.error.message || "Unexpected errorErrorMessage"}</ErrorMessage>}

        <h1>{sweep.name}</h1>

        <p><strong>{t("pages.sweeps.customer")}:</strong> {sweep.customer ? sweep.customer : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.sweeps.address")}:</strong> {sweep.address ? sweep.address : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.sweeps.report_number")}:</strong> {sweep.reportNumber ? sweep.reportNumber : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.sweeps.version")}:</strong> {sweep.version ? sweep.version : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={10} />
        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='pencil' color='primary' onClick={() => {
                dispatch(startEditingSweep(sweep));

                history.push(`/sweep/${sweep.id}/form`);
            }}>{t("common.buttons.edit")}</SimpleButton>

            <SimpleButton disabled={sweep.isExporting} size={'small'} prependIcon='download' color='info' loading={sweep.isExporting} onClick={() => {
                if (sweep.id) dispatch(sweepExport(sweep.id));
            }}>{sweep.isExporting ? t("pages.sweeps.buttons.exporting") : t("pages.sweeps.buttons.export")}</SimpleButton>
            <SimpleButton size={'small'} prependIcon={!sweep.archivedAt ? 'archive' : 'unarchive'} loading={sweep.isTogglingArchive} color='warning' onClick={() => {
                if (sweep.id && !sweep.archivedAt) dispatch(sweepArchive(sweep.id));
                if (sweep.id && sweep.archivedAt) dispatch(sweepUnarchive(sweep.id));
            }}>{sweep.isTogglingArchive ? (sweep.archivedAt ? t("pages.sweeps.buttons.unarchiving") : t("pages.sweeps.buttons.archiving")) : (sweep.archivedAt ? t("pages.sweeps.buttons.unarchive") : t("pages.sweeps.buttons.archive"))}</SimpleButton>
            <SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {
                dispatch(systemAddDialog({
                    title: t("pages.sweeps.dialogs.remove_sweep"),
                    message: t("pages.sweeps.dialogs.remove_sweep_message"),
                    actions: [{
                        text: t("pages.sweeps.buttons.remove_sweep_confirm"),
                        action: () => {
                            dispatch(sweepRemoveRequest(sweep.id || ""));
                        },
                        color: 'danger',
                        prependIcon: 'trash'
                    },
                    {
                        text: t("pages.sweeps.buttons.remove_sweep_cancel"),
                        action: () => { },
                        color: 'none'
                    }]
                }));
            }}>{t("common.buttons.delete")}</SimpleButton>
        </ButtonGroup>

        <h1>{t("pages.sweeps.rooms")}</h1>
        <RoomsList>
            {sweep.rooms.map(room => <CustomLink to={`/room/${sweep.id}/${room.id}`} key={`room-id-${room.id}`}><RoomItem room={room} /></CustomLink>)}
        </RoomsList>

        {sweep.rooms.length === 0 && <p>{t("pages.sweeps.zero_rooms")}</p>}

        <Spacer size={10} />

        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => {
                dispatch(startEditingSweepRoom({
                    name: "",
                    sweepId: sweep.id || "",
                    times: [],
                    items: [],
                    id: ""
                }))

                history.push(`/room/${sweep.id}/form`)
            }}>{t("pages.sweeps.buttons.add_room")}</SimpleButton>
        </ButtonGroup>

        <h1>{t("pages.sweeps.members")}</h1>

        {sweep.members.length === 0 && <p>{t("pages.sweeps.zero_members")}</p>}

        {sweep.members.length > 0 && <CustomTable>
            <thead>
                <tr>
                    <th style={{ minWidth: 250 }}>{t("pages.sweeps.members_name")}</th>
                    <th style={{ minWidth: 120 }}>{t("pages.sweeps.members_role")}</th>
                    <th style={{ minWidth: 150, width: 150 }}>{t("pages.sweeps.members_actions")}</th>
                </tr>
            </thead>
            <tbody>
                {sweep.members.map(member => <tr key={`member-id-${member.id}`} className={member.error ? 'error' : ''}>
                    {member.isDeleting && <>
                        <td colSpan={3}>{t("pages.sweeps.deleting")}</td>
                    </>}

                    {!member.isDeleting && <>
                        <td>
                            {member.name}
                            {member.error && <p>{member.error.message}</p>}
                        </td>
                        <td>{member.idUser
                            ? <CustomLink to={`/users/${member.idUser}`}>
                                <SimpleButton
                                    size={'small'}
                                    color='info'
                                    onClick={() => { }}>{t("pages.sweeps.buttons.see_user")}
                                </SimpleButton>
                            </CustomLink>
                            : member.role || ""
                        }
                        </td>
                        <td>
                            <ButtonGroup>
                                <SimpleButton size={'small'} prependIcon='pencil' disabled={(member.idUser && member.idUser !== "" ? true : false)} color='primary' onClick={() => {
                                    dispatch(startEditingSweepMember(member))
                                    history.push(`/guest-member/${sweep.id}/${member.id}/form`)
                                }}
                                >
                                    {t("common.buttons.edit")}
                                </SimpleButton>
                                <SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {

                                    return dispatch(systemAddDialog({
                                        title: t("pages.sweeps.dialogs.remove_guest"),
                                        message: t("pages.sweeps.dialogs.remove_guest_message"),
                                        actions: [{
                                            text: t("pages.sweeps.buttons.remove_guest_confirm"),
                                            action: () => {
                                                dispatch(sweepGuestRemoveRequest(member.id));
                                            },
                                            color: 'danger',
                                            prependIcon: 'trash'
                                        },
                                        {
                                            text: t("pages.sweeps.buttons.remove_guest_cancel"),
                                            action: () => { },
                                            color: 'none'
                                        }]
                                    }));

                                }}>{t("common.buttons.delete")}</SimpleButton>
                            </ButtonGroup>
                        </td>
                    </>}
                </tr>)}
            </tbody>
        </CustomTable>}
        <Spacer size={10} />
        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => {
                history.push(`/add-member/${sweep.id}/form`)
            }}>{t("pages.sweeps.buttons.add_user")}
            </SimpleButton>
            <SimpleButton size={'small'} prependIcon='plus' color='secondary' onClick={() => {
                dispatch(startEditingSweepMember({
                    name: "",
                    id: "",
                    sweepId: sweep.id || ""
                }))

                history.push(`/guest-member/${sweep.id}/form`)
            }}>{t("pages.sweeps.buttons.add_guest")}</SimpleButton>
        </ButtonGroup>

        {/* *** REGISTER DE WORK */}
        <h1>{t("pages.sweeps.work_register")}</h1>

        {sweep.times.length === 0 && <p>{t("pages.sweeps.zero_work_register")}</p>}

        {sweep.times.length > 0 && <CustomTable>
            <thead>
                <tr>
                    <th style={{ minWidth: 160 }}>{t("pages.sweeps.work_register_begining")}</th>
                    <th style={{ minWidth: 160 }}>{t("pages.sweeps.work_register_ending")}</th>
                    <th style={{ minWidth: 90 }}>{t("pages.sweeps.work_register_total")}</th>
                    <th style={{ minWidth: 250 }}>{t("pages.sweeps.work_register_observation")}</th>
                    <th style={{ minWidth: 150, width: 150 }}>{t("pages.sweeps.work_register_actions")}</th>
                </tr>
            </thead>
            <tbody>
                {sweep.times.map(time => <tr key={`time-id-${time.id}`}>
                    <td>{time.beginingAt && time.beginingAt.toLocaleString()}</td>
                    <td>{time.endingAt ? time.endingAt.toLocaleString() : <em>{t("common.undefined")}</em>}</td>
                    <td>{calculateTotal(time)}</td>
                    <td>{time.observation ? time.observation : <em>{t("common.undefined")}</em>}</td>
                    <td><SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {

                        return dispatch(systemAddDialog({
                            title: t("pages.sweeps.dialogs.remove_work_register"),
                            message: t("pages.sweeps.dialogs.remove_work_register_message"),
                            actions: [{
                                text: t("pages.sweeps.buttons.remove_work_register_confirm"),
                                action: () => {
                                    time.id && dispatch(sweepWorkRegisterRemoveRequest(time.id))
                                },
                                color: 'danger',
                                prependIcon: 'trash'
                            },
                            {
                                text: t("pages.sweeps.buttons.remove_work_register_cancel"),
                                action: () => { },
                                color: 'none'
                            }]
                        }));

                    }}>{t("common.buttons.delete")}</SimpleButton></td>
                </tr>)}
            </tbody>
        </CustomTable>}
        <Spacer size={10} />
        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => {
                dispatch(startEditingRegister({
                    id: '',
                    sweepId: sweep.id || "",
                }))

                history.push(`/sweep-work-register/${sweep.id}/form`)

            }}>{t("pages.sweeps.buttons.add_work_register")}</SimpleButton>
        </ButtonGroup>
    </>);
}

export default SweepDetails;